/**
 * The rows behind one figure on a report.
 *
 * It is handed the list the report counted, rather than a rule for finding
 * it again. That matters here more than elsewhere: every figure on the
 * sales report is scoped to the chosen period, and a modal that recomputed
 * "unowned" or "stalled" from the whole board would open on 112 rows under
 * a heading that said 24. One list, counted once, shown twice.
 */
import { useApp } from '../../store/AppContext.jsx';
import { parseDate, daysAgo, formatDate, periodWord } from '../../lib/dates.js';
import Icon from '../../lib/icons.jsx';
import Sheet from '../Sheet.jsx';
import { Avatar, StagePill, OwnerPill, EmptyState } from '../Primitives.jsx';
import PlanChip from '../PlanChip.jsx';
import { companyName, leadDetails, lastTouch, clientOfDeal, dealValue } from '../../domain/crm.js';

export default function ReportListView({
  title, note, tone = 'g-purple', icon = 'card', rows: list = [], onClose
}) {
  const { S, crm, rows, fmtMoney, setSheet } = useApp();

  /* What the list is worth, where any of it is priced. A total over rows
     that mostly carry no figure would be a smaller claim than it looks. */
  const priced = list.filter(d => { const v = dealValue(rows, d); return v.known && !v.free; });
  const worth = priced.reduce((t, d) => t + dealValue(rows, d).monthly, 0);
  const clients = list.filter(d => clientOfDeal(rows, d)).length;
  const unowned = list.filter(d => !d.owner).length;

  return (
    <Sheet icon={icon} tone={tone} title={title} onClose={onClose} closeLabel="Close" wide bodyClass="psv"
      hero={(
        <div className={`psvhero ${tone}`}>
          <div>
            <b>{list.length}</b>
            <small>{note}</small>
          </div>
          <div className="psvchips">
            {clients ? <span className="psvchip"><b>{clients}</b> already clients</span> : null}
            {unowned ? <span className="psvchip warn"><b>{unowned}</b> unowned</span> : null}
            {priced.length ? <span className="psvchip"><b>{fmtMoney(worth)}/mo</b> priced</span> : null}
          </div>
        </div>
      )}
    >
      {!list.length ? (
        <div style={{ gridColumn: '1/-1' }}>
          <EmptyState icon={icon} tone={tone} title="Nothing here">
            No row answers to this in the period you have chosen.
          </EmptyState>
        </div>
      ) : null}

      <div className="psvrows">
        {list.map(d => {
          const f = leadDetails(crm, d);
          const client = clientOfDeal(rows, d);
          const v = dealValue(rows, d);
          const touch = lastTouch(crm, d.id);
          const came = parseDate(d.created);
          const place = [f.city, f.country].filter(Boolean).join(', ');

          return (
            /* Not a <button>: the row carries the plan chip, which is one
               itself, and buttons cannot nest. See LeadRow in SalesOverview. */
            <div
              className="psvrow"
              key={d.id}
              role="button"
              tabIndex={0}
              onClick={() => setSheet({ kind: 'dealView', id: d.id })}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSheet({ kind: 'dealView', id: d.id });
                }
              }}
            >
              <Avatar name={d.title} />

              <div className="psvwho">
                <b>{d.title}</b>
                <small>{f.email || <span className="dim">no address on the row</span>}</small>
                <div className="psvmeta">
                  {companyName(crm, d.cid) ? (
                    <span><Icon name="building" />{companyName(crm, d.cid)}</span>
                  ) : null}
                  {place ? <span><Icon name="pin" />{place}</span> : null}
                  {d.source ? <span><Icon name="zap" />{d.source}</span> : null}
                  {f.landingPath ? <span><Icon name="link" />{f.landingPath}</span> : null}
                </div>
              </div>

              <div className="psvtags">
                <StagePill value={d.stage} />
                {client ? <span className="dealchip cli"><Icon name="check" />Client</span> : null}
                {!d.origin ? <span className="dealchip man"><Icon name="edit" />Manual</span> : null}
                <PlanChip list={f.productList} label={f.products} />
              </div>

              <div className="psvown">
                {d.owner ? <OwnerPill name={d.owner} /> : <span className="psvnone">Unowned</span>}
                <small>
                  {touch ? `${daysAgo(touch)}d since a note`
                    : came ? `asked ${formatDate(came, false, S.dateFmt)}` : ''}
                </small>
              </div>

              {/* The figure is the invoice, so the line under it has to say
                  what period that invoice covers. It used to say "a month"
                  whatever the cycle was, which was wrong about an annual
                  account by a factor of twelve; where the two differ it now
                  also gives the monthly rate the total above is built from. */}
              <div className="psvstat">
                <b>{v.free ? 'Free' : v.known ? fmtMoney(v.amount) : came ? `${daysAgo(came)}d` : '—'}</b>
                <small>
                  {v.free ? 'no charge'
                    : !v.known ? 'on the board'
                      : v.monthly === v.amount ? periodWord(v.cycle)
                        : `${periodWord(v.cycle)} · ${fmtMoney(v.monthly)} a month`}
                </small>
              </div>
            </div>
          );
        })}
      </div>

      {list.length ? (
        <p className="psvfoot">
          <Icon name="eye" />
          Open any row for the whole request — the queries, the campaign it
          came through and everything logged against it since.
        </p>
      ) : null}
    </Sheet>
  );
}
